/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState } from 'react';
import { Modal, Button, message } from 'antd';
import * as XLSX from 'xlsx';
import { services } from '@comall-backend-builder/core';
import { errorHandle } from '../../services';
import './spu-import-modal.less';

const api = services.api;
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const FIND_SPU_PAGE_SIZE = 1000;

export type SpuImportItem = {
    spuCode: string;
    goodsName: string;
    skuCount?: number;
};

type SpuImportModalProps = {
    visible: boolean;
    /** 当前表单已圈选的 SPU */
    existingList?: SpuImportItem[];
    /** 归属当前规则的 SPU（含已从列表移除的） */
    ownedSpuCodes?: string[];
    onCancel: () => void;
    onSuccess: (items: SpuImportItem[]) => void;
};

const formatTimestamp = () => {
    const d = new Date();
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return (
        `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
        `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
    );
};

const exportResultExcel = (rows: Array<{ spu编码: string; 执行结果: string }>) => {
    const sheetData = [
        ['spu编码', '执行结果'],
        ...rows.map((item) => [item.spu编码, item.执行结果]),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet['!cols'] = [{ wch: 28 }, { wch: 28 }];
    worksheet['!rows'] = sheetData.map(() => ({ hpt: 28 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'sheet');
    XLSX.writeFile(workbook, `移动端详情SPU批量导入结果_${formatTimestamp()}.xlsx`);
};

const fetchSpuByCodes = async (spuCodes: string[]) => {
    const uniqueCodes = Array.from(new Set(spuCodes.filter(Boolean)));
    const map = new Map<string, any>();

    for (let i = 0; i < uniqueCodes.length; i += FIND_SPU_PAGE_SIZE) {
        const chunk = uniqueCodes.slice(i, i + FIND_SPU_PAGE_SIZE);
        const res: any = await api.get(
            { page: 1, per_page: FIND_SPU_PAGE_SIZE, spus: chunk },
            { apiPath: '/products/search/find_spu_rich_text' }
        );
        const list = res?.result || res?.data || [];
        list.forEach((item: any) => {
            const spuCode = item.spuCode || item.spu || '';
            if (spuCode) {
                map.set(String(spuCode), item);
            }
        });
    }

    return map;
};

const parseSpuCodesFromFile = (file: File): Promise<string[]> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const result = event.target?.result;
                const workbook = XLSX.read(result, { type: 'binary' });
                const firstSheetName = workbook.SheetNames[0];
                if (!firstSheetName) {
                    reject(new Error('parse'));
                    return;
                }
                const sheet = workbook.Sheets[firstSheetName];
                const data: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                if (!data.length) {
                    resolve([]);
                    return;
                }
                const hasHeader = Object.keys(data[0] || {}).some(
                    (key) => key === 'spu编码' || key === 'SPU编号'
                );
                if (!hasHeader) {
                    reject(new Error('parse'));
                    return;
                }
                const codes = data
                    .map((row) => String(row['spu编码'] || row['SPU编号'] || '').trim())
                    .filter(Boolean);
                resolve(codes);
            } catch (e) {
                reject(new Error('parse'));
            }
        };
        reader.onerror = () => reject(new Error('parse'));
        reader.readAsBinaryString(file);
    });

export const BatchDetailSpuImportModal: React.FC<SpuImportModalProps> = ({
    visible,
    existingList = [],
    ownedSpuCodes = [],
    onCancel,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const existingCodeSet = new Set(existingList.map((item) => item.spuCode));
    const ownedCodeSet = new Set(ownedSpuCodes.filter(Boolean));

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // 允许重复选择同一文件
        e.target.value = '';
        if (!file) {
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            message.warning('文件过大，请上传不超过 2MB 的文件');
            return;
        }

        const ext = file.name
            .split('.')
            .pop()
            ?.toLowerCase();
        if (!ext || !['xlsx', 'xls', 'csv'].includes(ext)) {
            message.warning('文件解析失败，请使用模板格式上传');
            return;
        }

        setLoading(true);
        try {
            const codes = await parseSpuCodesFromFile(file);
            if (!codes.length) {
                message.warning('未读取到有效的 SPU 编码');
                return;
            }

            let spuMap: Map<string, any>;
            try {
                spuMap = await fetchSpuByCodes(codes);
            } catch (err) {
                message.error('获取 SPU 校验数据失败，请重试');
                errorHandle(err);
                return;
            }

            const fileCountMap = new Map<string, number>();
            codes.forEach((code) => {
                fileCountMap.set(code, (fileCountMap.get(code) || 0) + 1);
            });

            const resultRows: Array<{ spu编码: string; 执行结果: string }> = [];
            const successItems: SpuImportItem[] = [];
            const successCodeSet = new Set<string>();

            codes.forEach((code) => {
                if ((fileCountMap.get(code) || 0) > 1) {
                    resultRows.push({ spu编码: code, 执行结果: '失败：文件中重复数据' });
                    return;
                }

                const found = spuMap.get(code);
                if (!found) {
                    resultRows.push({ spu编码: code, 执行结果: '失败：SPU编码不存在' });
                    return;
                }

                if (existingCodeSet.has(code) || successCodeSet.has(code)) {
                    resultRows.push({ spu编码: code, 执行结果: '失败：SPU已存在' });
                    return;
                }

                if (found.hasRichText && !ownedCodeSet.has(code)) {
                    resultRows.push({ spu编码: code, 执行结果: '失败：该SPU已有规则' });
                    return;
                }

                resultRows.push({ spu编码: code, 执行结果: '成功' });
                successCodeSet.add(code);
                successItems.push({
                    spuCode: code,
                    goodsName: found.goodsName || found.name || '',
                    skuCount: found.skuCount,
                });
            });

            exportResultExcel(resultRows);

            if (successItems.length) {
                message.success(`成功导入 ${successItems.length} 个 SPU，已下载导入结果`);
                onSuccess(successItems);
            } else {
                message.warning('未成功导入 SPU，已下载导入结果');
                onCancel();
            }
        } catch (err) {
            if ((err as Error)?.message === 'parse') {
                message.warning('文件解析失败，请使用模板格式上传');
            } else {
                message.error('获取 SPU 校验数据失败，请重试');
                errorHandle(err);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="批量导入"
            visible={visible}
            onCancel={onCancel}
            destroyOnClose
            maskClosable={false}
            confirmLoading={loading}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    取消
                </Button>,
            ]}
            className="batch-detail-spu-import"
        >
            <Button
                type="primary"
                loading={loading}
                className="batch-detail-spu-import__upload"
                onClick={() => inputRef.current?.click()}
            >
                选择文件
            </Button>
            <input
                ref={inputRef}
                className="batch-detail-spu-import__file"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
            />
            <p className="batch-detail-spu-import__tip">
                支持 .xlsx / .xls / .csv，文件不超过 2MB，请使用「移动端详情SPU导入模板」
            </p>
        </Modal>
    );
};

export default BatchDetailSpuImportModal;
